import { FullStopIcon } from 'BreetAssets';
import { DropdownButton, type IDropdownOption, PageHeader, SearchInput } from 'BreetComponents';
import { getQueryParamValue, updateUrlSearchParams } from 'BreetHelpers';
import debounce from 'lodash/debounce';
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { type IDataTableHeader, type TableHeaderFilterItemOptions } from './DataTable.helpers';

export const DataTableHeader = ({
	filterProps,
	exportProps,
	filterList,
	pageTitle,
	isFilter,
	searchProps,
	onSelectedFilterChange,
	isLoading,
	onRefresh,
	tip,
}: IDataTableHeader) => {
	const [selectedFilters, setSelectedFilters] = useState<Record<string, TableHeaderFilterItemOptions | undefined>>({});

	const [searchValue, setSearchValue] = useState<string>('');

	const debouncedSearch = useMemo(
		() =>
			debounce((onSearch: (data: Record<string, string>) => void, searchKey: string | number, value: string) => {
				onSearch({ [String(searchKey)]: value });
			}, 300),
		[]
	);

	useEffect(() => {
		if (onSelectedFilterChange) onSelectedFilterChange(selectedFilters);
	}, [onSelectedFilterChange, selectedFilters]);

	useEffect(() => {
		filterList?.forEach((filter) => {
			const encodedFilterOptions = getQueryParamValue(filter.id);
			if (encodedFilterOptions) {
				const decodedFilterOptions = JSON.parse(decodeURIComponent(encodedFilterOptions)) as TableHeaderFilterItemOptions;
				setSelectedFilters({ [filter.id]: decodedFilterOptions });
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleSelectedFilters = useCallback((filterKey: string, selectedItem: IDropdownOption) => {
		setSelectedFilters((prevData) => {
			const { [filterKey]: existingEntry, ...newFilters } = prevData;

			if (JSON.stringify(existingEntry) === JSON.stringify(selectedItem.value)) {
				return { ...newFilters, [filterKey]: undefined };
			}

			return { ...newFilters, [filterKey]: selectedItem.value } as Record<string, TableHeaderFilterItemOptions | undefined>;
		});
	}, []);

	const handleOptionClick = useCallback(
		(filterKey: string) => (selectedItem?: IDropdownOption) => {
			if (!selectedItem) return;
			toggleSelectedFilters(filterKey, selectedItem);
			updateUrlSearchParams({ addParams: { [filterKey]: encodeURIComponent(JSON.stringify(selectedItem.value)) } });
		},
		[toggleSelectedFilters]
	);

	const onHandleSearch = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setSearchValue(e.target.value);

			if (searchProps?.handleReqSearch) {
				debouncedSearch(searchProps.handleReqSearch, searchProps.searchKey ?? 'search', e.target.value);
			}
		},
		[searchProps, debouncedSearch]
	);

	return (
		<div className='datatable_header'>
			<PageHeader
				pageTitle={pageTitle}
				isLoading={isLoading}
				onRefresh={onRefresh}
				tip={tip}
			/>
			<div className='datatable_headersearchfilter'>
				{!!searchProps && (
					<SearchInput
						widthType='full'
						id=''
						onChange={onHandleSearch}
						placeholder={searchProps.placeHolder}
						value={searchValue}
					/>
				)}
				{!!filterProps && (
					<DropdownButton
						label={filterProps.label}
						leadingIcon={filterProps.leadingIcon}
						trailingIcon={isFilter ? <FullStopIcon /> : undefined}
						variant={filterProps.variant}
						dropDownComponent={filterProps.dropDownComponent}
						switchMobileWidth={filterProps.switchMobileWidth}
						switchLeadingMobileIcon
						onChange={handleOptionClick(filterProps.filterKey ?? '')}
					/>
				)}
				{exportProps && (
					<DropdownButton
						label={exportProps.label}
						leadingIcon={exportProps.leadingIcon}
						switchMobileWidth={exportProps.switchMobileWidth}
						switchLeadingMobileIcon={exportProps.switchLeadingMobileIcon}
						switchTrailingMobileIcon={exportProps.switchTrailingMobileIcon}
						disabled
					/>
				)}
			</div>
		</div>
	);
};
